// User Management API - Connected to Backend via API Gateway
// Comprehensive user management for organizations

import { authenticatedFetch } from './authenticatedFetch';

// User types
export interface UserDetails {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  avatar?: string;
  is_active: boolean;
  is_verified: boolean;
  timezone: string;
  locale?: string;
  auth_provider?: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
  organizations: {
    id: string;
    name: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
    permissions: Record<string, any>;
    organization: {
      id: string;
      name: string;
      plan_type: string;
    };
  }[];
  preferences?: {
    theme: 'light' | 'dark' | 'system';
    notifications: {
      email_alerts: boolean;
      security_alerts: boolean;
      marketing_emails: boolean;
    };
    language: string;
  };
}

export interface UserUsage {
  user_id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  service_usage: {
    [service: string]: {
      total_requests: number;
      total_cost: number;
      daily_usage: {
        date: string;
        requests: number;
        cost: number;
      }[];
    };
  };
  total_requests: number;
  total_cost: number;
}

export interface UserSettings {
  user_id: string;
  timezone: string;
  preferences: {
    theme: 'light' | 'dark' | 'system';
    notifications: {
      email_alerts: boolean;
      security_alerts: boolean;
      marketing_emails: boolean;
    };
    language: string;
    dashboard_layout?: string;
    [key: string]: any;
  };
}

export interface UserSession {
  id: string;
  user_id: string;
  ip_address: string;
  user_agent: string;
  location?: string;
  is_current: boolean;
  created_at: string;
  last_activity: string;
  expires_at: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface UpdateUserSettingsRequest {
  timezone?: string;
  preferences?: {
    theme?: 'light' | 'dark' | 'system';
    notifications?: {
      email_alerts?: boolean;
      security_alerts?: boolean;
      marketing_emails?: boolean;
    };
    language?: string;
    dashboard_layout?: string;
    [key: string]: any;
  };
}

// Wrapper function using shared authenticated fetch
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return authenticatedFetch<T>(endpoint, { ...options, serviceName: 'User API' });
}

// User Management API
export const userAPI = {
  /**
   * Get current user details
   */
  async getCurrentUser(): Promise<UserDetails> {
    return await apiRequest<UserDetails>('/auth/me', {
      method: 'GET',
    });
  },

  /**
   * Update current user profile
   */
  async updateProfile(data: {
    first_name?: string;
    last_name?: string;
    timezone?: string;
  }): Promise<UserDetails> {
    return await apiRequest<UserDetails>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Change user password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    // Backend expects form-encoded data for password change
    const formData = new FormData();
    formData.append('current_password', data.current_password);
    formData.append('new_password', data.new_password);

    return await apiRequest<void>('/auth/change-password', {
      method: 'POST',
      headers: {
        // Remove Content-Type to let browser set it for FormData
      },
      body: formData,
    });
  },

  /**
   * Get user settings
   */
  async getUserSettings(): Promise<UserSettings> {
    return await apiRequest<UserSettings>('/auth/settings', {
      method: 'GET',
    });
  },

  /**
   * Update user settings
   */
  async updateUserSettings(data: UpdateUserSettingsRequest): Promise<UserSettings> {
    return await apiRequest<UserSettings>('/auth/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get user usage statistics
   */
  async getUserUsage(params?: {
    organization_id?: string;
    start_date?: string;
    end_date?: string;
    service_name?: string;
  }): Promise<UserUsage> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.service_name) queryParams.append('service_name', params.service_name);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/auth/usage/user/me?${queryString}` : `/auth/usage/user/me`;
    
    return await apiRequest<UserUsage>(url, {
      method: 'GET',
    });
  },

  /**
   * Get user usage for specific user (admin only)
   */
  async getUserUsageById(userId: string, params?: {
    organization_id?: string;
    start_date?: string;
    end_date?: string;
    service_name?: string;
  }): Promise<UserUsage> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.service_name) queryParams.append('service_name', params.service_name);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/auth/usage/user/${userId}?${queryString}` : `/auth/usage/user/${userId}`;
    
    return await apiRequest<UserUsage>(url, {
      method: 'GET',
    });
  },

  /**
   * Get user's active sessions
   */
  async getUserSessions(): Promise<UserSession[]> {
    return await apiRequest<UserSession[]>('/auth/sessions', {
      method: 'GET',
    });
  },

  /**
   * Revoke a specific session
   */
  async revokeSession(sessionId: string): Promise<void> {
    return await apiRequest<void>(`/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Revoke all other sessions (keep current session)
   */
  async revokeAllOtherSessions(): Promise<void> {
    return await apiRequest<void>('/auth/sessions', {
      method: 'DELETE',
    });
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<void> {
    // Backend expects form-encoded data
    const formData = new FormData();
    formData.append('email', email);

    return await apiRequest<void>('/auth/forgot-password', {
      method: 'POST',
      headers: {
        // Remove Content-Type to let browser set it for FormData
      },
      body: formData,
    });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    // Backend expects form-encoded data
    const formData = new FormData();
    formData.append('token', token);
    formData.append('new_password', newPassword);

    return await apiRequest<void>('/auth/reset-password', {
      method: 'POST',
      headers: {
        // Remove Content-Type to let browser set it for FormData
      },
      body: formData,
    });
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    // Backend expects form-encoded data
    const formData = new FormData();
    formData.append('token', token);

    return await apiRequest<void>('/auth/verify-email', {
      method: 'POST',
      headers: {
        // Remove Content-Type to let browser set it for FormData
      },
      body: formData,
    });
  },

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<void> {
    return await apiRequest<void>('/auth/resend-verification', {
      method: 'POST',
    });
  },

  /**
   * Get users (admin only) - Returns aggregated user statistics
   */
  async getUsers(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    organization_id?: string;
  }): Promise<{
    users: UserDetails[];
    total: number;
    active: number;
    pending: number;
    inactive: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.organization_id) queryParams.append('organization_id', params.organization_id);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/admin/users?${queryString}` : `/admin/users`;
    
    return await apiRequest<{
      users: UserDetails[];
      total: number;
      active: number;
      pending: number;
      inactive: number;
    }>(url, {
      method: 'GET',
    });
  },

  /**
   * Get user by ID (admin only)
   */
  async getUserById(userId: string): Promise<UserDetails> {
    return await apiRequest<UserDetails>(`/admin/users/${userId}`, {
      method: 'GET',
    });
  },

  /**
   * Update user status (admin only)
   */
  async updateUserStatus(userId: string, data: {
    is_active?: boolean;
    is_verified?: boolean;
  }): Promise<UserDetails> {
    return await apiRequest<UserDetails>(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete user (admin only)
   */
  async deleteUser(userId: string): Promise<void> {
    return await apiRequest<void>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Update phone number
   * Matches backend API: PUT /auth/phone
   */
  async updatePhoneNumber(phoneNumber: string): Promise<{
    success: boolean;
    message: string;
    phone_verified: boolean;
  }> {
    return await apiRequest('/auth/phone', {
      method: 'PUT',
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  },

  /**
   * Request phone verification code
   * Matches backend API: POST /auth/phone/request-verification
   */
  async requestPhoneVerification(): Promise<{
    success: boolean;
    message: string;
  }> {
    return await apiRequest('/auth/phone/request-verification', {
      method: 'POST',
    });
  },

  /**
   * Verify phone number with code
   * Matches backend API: POST /auth/phone/verify
   */
  async verifyPhoneNumber(code: string): Promise<{
    success: boolean;
    message: string;
    phone_verified: boolean;
  }> {
    return await apiRequest('/auth/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },
};